import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { blogApi } from '../../apis/blog.api';

// Async thunks
export const fetchBlogs = createAsyncThunk(
    'blog/fetchBlogs',
    async (params, { rejectWithValue }) => {
        try {
            const response = await blogApi.getAllBlogs(params);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch blogs');
        }
    },
);

export const createBlog = createAsyncThunk(
    'blog/createBlog',
    async (blogData, { rejectWithValue }) => {
        try {
            const response = await blogApi.createBlog(blogData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to create blog');
        }
    },
);

export const updateBlog = createAsyncThunk(
    'blog/updateBlog',
    async ({ id, blogData }, { rejectWithValue }) => {
        try {
            const response = await blogApi.updateBlog(id, blogData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to update blog');
        }
    },
);

export const deleteBlog = createAsyncThunk(
    'blog/deleteBlog',
    async (id, { rejectWithValue }) => {
        try {
            await blogApi.deleteBlog(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to delete blog');
        }
    },
);

export const fetchBlogCategories = createAsyncThunk(
    'blog/fetchCategories',
    async (_, { rejectWithValue }) => {
        try {
            const response = await blogApi.getAllCategories();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch categories');
        }
    },
);

export const createBlogCategory = createAsyncThunk(
    'blog/createCategory',
    async (categoryData, { rejectWithValue }) => {
        try {
            const response = await blogApi.createCategory(categoryData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to create category');
        }
    },
);

export const updateBlogCategory = createAsyncThunk(
    'blog/updateCategory',
    async ({ id, categoryData }, { rejectWithValue }) => {
        try {
            const response = await blogApi.updateCategory(id, categoryData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to update category');
        }
    },
);

export const deleteBlogCategory = createAsyncThunk(
    'blog/deleteCategory',
    async (id, { rejectWithValue }) => {
        try {
            await blogApi.deleteCategory(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to delete category');
        }
    },
);

export const fetchBlogTags = createAsyncThunk(
    'blog/fetchTags',
    async (_, { rejectWithValue }) => {
        try {
            const response = await blogApi.getAllTags();
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to fetch tags');
        }
    },
);

export const createBlogTag = createAsyncThunk(
    'blog/createTag',
    async (tagData, { rejectWithValue }) => {
        try {
            const response = await blogApi.createTag(tagData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to create tag');
        }
    },
);

export const updateBlogTag = createAsyncThunk(
    'blog/updateTag',
    async ({ id, tagData }, { rejectWithValue }) => {
        try {
            const response = await blogApi.updateTag(id, tagData);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to update tag');
        }
    },
);

export const deleteBlogTag = createAsyncThunk(
    'blog/deleteTag',
    async (id, { rejectWithValue }) => {
        try {
            await blogApi.deleteTag(id);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to delete tag');
        }
    },
);

export const searchBlogs = createAsyncThunk(
    'blog/searchBlogs',
    async (searchTerm, { rejectWithValue }) => {
        try {
            const response = await blogApi.searchBlogs(searchTerm);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data || 'Failed to search blogs');
        }
    },
);

// Initial state
const initialState = {
    blogs: [],
    categories: [],
    tags: [],
    searchResults: [],
    loading: false,
    error: null,
    pagination: {
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    },
    filters: {
        status: '',
        category: '',
        tags: [],
        author: '',
        is_featured: '',
        keywords: '',
    },
    sort: {
        sortBy: 'published_at',
        sortOrder: -1,
    },
};

// Blog slice
const blogSlice = createSlice({
    name: 'blog',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setFilters: (state, action) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        setSort: (state, action) => {
            state.sort = { ...state.sort, ...action.payload };
        },
        clearSearchResults: (state) => {
            state.searchResults = [];
        },
        resetBlogState: (state) => {
            state.blogs = [];
            state.searchResults = [];
            state.loading = false;
            state.error = null;
            state.pagination = {
                page: 1,
                limit: 10,
                total: 0,
                totalPages: 0,
            };
        },
    },
    extraReducers: (builder) => {
        // Fetch blogs
        builder
            .addCase(fetchBlogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBlogs.fulfilled, (state, action) => {
                state.loading = false;
                const data = action.payload?.result || {};
                state.blogs = data.blogs || [];
                state.pagination = {
                    page: data.page || 1,
                    limit: data.limit || 10,
                    total: data.total || 0,
                    totalPages: data.totalPages || 0,
                };
            })
            .addCase(fetchBlogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Create blog
        builder
            .addCase(createBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createBlog.fulfilled, (state, action) => {
                state.loading = false;
                state.blogs.unshift(action.payload?.result || action.payload);
            })
            .addCase(createBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Update blog
        builder
            .addCase(updateBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateBlog.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.blogs.findIndex(
                    (blog) => blog._id === action.payload._id,
                );
                if (index !== -1) {
                    state.blogs[index] = action.payload;
                }
            })
            .addCase(updateBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Delete blog
        builder
            .addCase(deleteBlog.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteBlog.fulfilled, (state, action) => {
                state.loading = false;
                state.blogs = state.blogs.filter((blog) => blog._id !== action.payload);
            })
            .addCase(deleteBlog.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Fetch categories
        builder
            .addCase(fetchBlogCategories.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBlogCategories.fulfilled, (state, action) => {
                state.loading = false;
                state.categories = action.payload?.result || [];
            })
            .addCase(fetchBlogCategories.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Create category
        builder.addCase(createBlogCategory.fulfilled, (state, action) => {
            state.categories.push(action.payload);
        });

        // Update category
        builder.addCase(updateBlogCategory.fulfilled, (state, action) => {
            const index = state.categories.findIndex(
                (cat) => cat._id === action.payload._id,
            );
            if (index !== -1) {
                state.categories[index] = action.payload;
            }
        });

        // Delete category
        builder.addCase(deleteBlogCategory.fulfilled, (state, action) => {
            state.categories = state.categories.filter(
                (cat) => cat._id !== action.payload,
            );
        });

        // Fetch tags
        builder
            .addCase(fetchBlogTags.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchBlogTags.fulfilled, (state, action) => {
                state.loading = false;
                state.tags = action.payload?.result || [];
            })
            .addCase(fetchBlogTags.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });

        // Create tag
        builder.addCase(createBlogTag.fulfilled, (state, action) => {
            state.tags.push(action.payload);
        });

        // Update tag
        builder.addCase(updateBlogTag.fulfilled, (state, action) => {
            const index = state.tags.findIndex((tag) => tag._id === action.payload._id);
            if (index !== -1) {
                state.tags[index] = action.payload;
            }
        });

        // Delete tag
        builder.addCase(deleteBlogTag.fulfilled, (state, action) => {
            state.tags = state.tags.filter((tag) => tag._id !== action.payload);
        });

        // Search blogs
        builder
            .addCase(searchBlogs.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(searchBlogs.fulfilled, (state, action) => {
                state.loading = false;
                state.searchResults = action.payload?.result || [];
            })
            .addCase(searchBlogs.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            });
    },
});

export const {
    setLoading,
    setError,
    clearError,
    setPagination,
    setFilters,
    setSort,
    clearSearchResults,
    resetBlogState,
} = blogSlice.actions;

export default blogSlice.reducer;
